import {
  Service,
  createEditService,
  deleteService,
  getServices,
  serviceKeys,
  subscribeToServices,
  swapServicePositions,
} from '@/api/apiServices';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'react-hot-toast';

export function useServices() {
  const queryClient = useQueryClient();

  // Query for fetching services
  const servicesQuery = useQuery({
    queryKey: serviceKeys.lists(),
    queryFn: getServices,
  });

  // Mutation for creating/editing a service
  const createEditMutation = useMutation({
    mutationFn: ({ service, id }: { service: Partial<Service>; id?: number }) =>
      createEditService(service, id),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      toast.success(id ? 'Service updated successfully' : 'Service created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error saving service: ${error.message}`);
    },
  });

  // Mutation for deleting a service
  const deleteMutation = useMutation({
    mutationFn: deleteService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      toast.success('Service deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error deleting service: ${error.message}`);
    },
  });

  // Mutation for swapping service positions
  const swapPositionMutation = useMutation({
    mutationFn: ({
      currentRow,
      targetRow,
      direction,
    }: {
      currentRow: Service;
      targetRow: Service;
      direction: 'up' | 'down';
    }) => swapServicePositions(currentRow, targetRow, direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      toast.success('Service order updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error reordering services: ${error.message}`);
    },
  });

  // Set up realtime subscription
  useEffect(() => {
    const unsubscribe = subscribeToServices(
      // Update handler
      ({ new: newService, old: oldService }) => {
        queryClient.setQueryData<Service[]>(serviceKeys.lists(), oldData => {
          if (!oldData) return oldData;
          return oldData.map(service =>
            service.id === oldService.id ? { ...service, ...newService } : service
          );
        });
      },
      // Delete handler
      ({ old: oldService }) => {
        queryClient.setQueryData<Service[]>(serviceKeys.lists(), oldData => {
          if (!oldData) return oldData;
          return oldData.filter(service => service.id !== oldService.id);
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  return {
    services: servicesQuery.data ?? [],
    isLoading: servicesQuery.isLoading,
    error: servicesQuery.error as Error | null,
    createEditService: createEditMutation.mutate,
    isCreatingOrEditing: createEditMutation.isPending,
    deleteService: deleteMutation.mutate,
    swapServicePosition: swapPositionMutation.mutate,
  };
}
